import { useEffect, useRef } from 'react';
import { useUserClientContext } from '@/app/clientContext';
import { Box, Divider, Paper, useMantineTheme } from '@mantine/core';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useInternalPageAccess } from '@/hooks/useInternalPageAccess';
import { MenuItem } from './MenuItem';
import { Loader, Lock, Shield } from 'react-feather';
import classes from './ProfileMenu.module.css';
import Modal from '@/components/Modals/Modal';
import { useAuthContext } from '@/app/auth';
import { INTERNAL_PATH } from '@/app/constants';

const LogoutMenuItem = () => {
  const { logOut, isLoggingOut } = useAuthContext();
  return (
    <MenuItem
      Component={isLoggingOut ? Loader : Lock}
      label="Logout"
      onClick={logOut}
    />
  );
};

interface ProfileMenuProps {
  isVisible: boolean;
  hideProfileMenuFn: () => void;
}

export const ProfileMenu = ({
  isVisible,
  hideProfileMenuFn,
}: ProfileMenuProps) => {
  const { actualLoginEmail } = useUserClientContext();
  const internalPageAccess = useInternalPageAccess();
  const theme = useMantineTheme();
  const isSmallScreen = useSmallScreen();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        e.target instanceof Node &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !menuRef.current.parentElement?.contains(e.target) // check that the profile menu button is not being clicked
      ) {
        hideProfileMenuFn();
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [hideProfileMenuFn]);

  const menuContent = (
    <Paper
      className={classes.container}
      ref={menuRef}
      shadow={theme.other.shadow.mainColorBackgroundPrimary}
    >
      <Box className={classes.header} visibleFrom="lg">
        {actualLoginEmail}
      </Box>
      <Divider color={theme.other.mainColor.background.tertiary} />
      {internalPageAccess === 'allowed' && (
        <MenuItem Component={Shield} label="Admin" to={INTERNAL_PATH} />
      )}
      <LogoutMenuItem />
    </Paper>
  );
  if (isSmallScreen)
    return (
      <Modal
        opened={isVisible}
        onClose={hideProfileMenuFn}
        title={actualLoginEmail}
        classNames={{
          body: classes.modalBody,
          content: classes.modalContent,
        }}
      >
        {menuContent}
      </Modal>
    );

  return menuContent;
};
